import { action, computed, observable } from 'mobx'
import { isValid } from 'date-fns'
import type {
  DateTimeAnswer,
  DateTimeQuestion,
} from '@/features/jobs/questions/questions'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

/**
 * Question answer editor for `DateTime` questions.
 */
export class DateTimeQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<DateTimeQuestion> {
  /** @inheritDoc */
  readonly type = 'DateTime'

  @observable accessor value: Date | null = null
  @observable accessor collectTime = false
  @observable accessor disallowPast = false
  @observable accessor disallowFuture = false

  constructor(private readonly _getAsOfDate: () => Date) {
    super()
  }

  /** @inheritDoc */
  @computed get isAnswered(): boolean {
    return isValid(this.value)
  }

  /** @inheritDoc */
  @computed get answer(): DateTimeAnswer | null {
    return this.value ? { type: 'DateTime', value: this.value } : null
  }

  /**
   * The earliest date that can be picked: the as-of date when past dates are
   * disallowed.
   */
  @computed get minDate(): Date | undefined {
    return this.disallowPast ? this._getAsOfDate() : undefined
  }

  /**
   * The latest date that can be picked: now when future dates are disallowed.
   * Not a computed, so "now" is re-read on every render.
   */
  get maxDate(): Date | undefined {
    return this.disallowFuture ? new Date() : undefined
  }

  /** @inheritDoc */
  set(question: DateTimeQuestion): void {
    this.collectTime = question.collectTime
    this.disallowPast = question.disallowPast
    this.disallowFuture = question.disallowFuture
    this.value = question.answer?.value ?? null
  }

  /**
   * Responds to the question.
   *
   * @param value
   */
  @action.bound
  respond(value: Date | undefined) {
    const next = value ?? null
    if (this.value === next) {
      return
    }

    this.value = next
    this.notifyAnswerChanged()
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    return this.isAnswered
      ? QuestionValidationResult.ok()
      : QuestionValidationResult.failed(['This field is required.'])
  }

  /** @inheritDoc */
  protected formatTextAnswer(): string {
    if (!this.value) {
      return ''
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      ...(this.collectTime && {
        hour: 'numeric',
        minute: '2-digit',
      }),
    })

    return formatter.format(this.value)
  }
}
